// src/components/DataCenter/homeChartPresets.js
// Reshape the raw ApexCharts payloads from data.tablemate.net/analyse into
// cleaner dashboard charts. Each preset takes (serverOptions, serverSeries)
// and returns { options, series } ready for <Chart>.

const BRAND = "#e30613";
const TEXT_MUTED = "#6b7280";
const GRID = "#eef0f3";
const FONT = "Montserrat, sans-serif";
const MAX_CATEGORIES = 8;

const toNum = (v) => {
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const fmtMoney = (v, digits = 2) =>
  `BWP ${toNum(v).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;

const fmtMoneyShort = (v) => {
  const n = toNum(v);
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return n.toFixed(0);
};

// "mains_and_psita" -> "Mains and psita"
const prettyName = (s) => {
  const t = String(s || "").replace(/[_-]+/g, " ").trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : "Uncategorised";
};

// "January" -> "Jan"; leaves other labels alone.
const shortMonth = (c) =>
  /^(January|February|March|April|May|June|July|August|September|October|November|December)$/.test(c)
    ? c.slice(0, 3)
    : c;

// Hour labels ("6 AM", "12 PM") mean an hourly view.
const isHourly = (cats) =>
  cats.length > 0 && cats.every((c) => /^\d{1,2} (AM|PM)$/.test(String(c)));

// Keep only the span between the first and last hour that has data,
// so a quiet morning or overnight doesn't fill most of the axis.
function trimToActive(categories, seriesData) {
  const has = (i) => seriesData.some((d) => d[i] != null && d[i] !== 0);
  let first = 0;
  let last = categories.length - 1;
  while (first <= last && !has(first)) first += 1;
  while (last >= first && !has(last)) last -= 1;
  if (first > last) return { categories: [], seriesData: seriesData.map(() => []) };
  // One hour of padding on each side reads better than a bar touching the edge.
  first = Math.max(0, first - 1);
  last = Math.min(categories.length - 1, last + 1);
  return {
    categories: categories.slice(first, last + 1),
    seriesData: seriesData.map((d) => d.slice(first, last + 1)),
  };
}

function sumSeries(series, match) {
  const picked = series.filter((s) => match.test(String(s?.name || "")));
  if (!picked.length) return [];
  const len = Math.max(...picked.map((s) => (s.data || []).length));
  return Array.from({ length: len }, (_, i) =>
    picked.reduce((acc, s) => acc + toNum((s.data || [])[i]), 0)
  );
}

function base({ type, title, subtitle, horizontal = false }) {
  return {
    chart: {
      type,
      fontFamily: FONT,
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: true, speed: 300 },
    },
    colors: [BRAND],
    title: {
      text: title,
      align: "left",
      style: { fontSize: "15px", fontWeight: 600, color: "#111827" },
    },
    subtitle: {
      text: subtitle,
      align: "left",
      style: { fontSize: "12px", color: TEXT_MUTED },
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    grid: {
      borderColor: GRID,
      strokeDashArray: 0,
      xaxis: { lines: { show: horizontal } },
      yaxis: { lines: { show: !horizontal } },
      padding: { left: 8, right: 8 },
    },
    plotOptions: {
      bar: {
        horizontal,
        borderRadius: 3,
        borderRadiusApplication: "end",
        columnWidth: "60%",
        barHeight: "65%",
      },
    },
    states: { active: { filter: { type: "none" } } },
    noData: {
      text: "No data for this period",
      style: { color: TEXT_MUTED, fontSize: "13px", fontFamily: FONT },
    },
  };
}

const axisLabel = { style: { colors: TEXT_MUTED, fontSize: "11px" } };

/* ---------- Revenue over time (cid 1 / chid 1) ---------- */
// The server also sends a "dining sessions" series, but for most periods it
// counts waiter-by-hour groups rather than sessions, so only revenue is shown.
export function revenueOverTime(serverOptions = {}, serverSeries = []) {
  let categories = (serverOptions?.xaxis?.categories || []).map(String);
  let revenue = sumSeries(serverSeries, /revenue/i);

  if (isHourly(categories)) {
    const t = trimToActive(categories, [revenue]);
    categories = t.categories;
    revenue = t.seriesData[0];
  }

  const total = revenue.reduce((a, b) => a + toNum(b), 0);
  const hasData = total > 0;
  const isDaily = categories.length && /^\d{4}-\d{2}-\d{2}$/.test(categories[0]);
  const manyPoints = categories.length > 45;

  const opts = base({
    type: manyPoints ? "area" : "bar",
    title: "Revenue",
    subtitle: hasData ? `${fmtMoney(total)} total` : "",
  });

  return {
    options: {
      ...opts,
      stroke: manyPoints ? { curve: "smooth", width: 2 } : { width: 0 },
      fill: manyPoints
        ? { type: "gradient", gradient: { opacityFrom: 0.35, opacityTo: 0.02 } }
        : { opacity: 1 },
      xaxis: {
        categories: isDaily
          ? categories.map((d) =>
              new Date(`${d}T00:00:00`).toLocaleDateString(undefined, {
                day: "numeric",
                month: "short",
              })
            )
          : categories.map(shortMonth),
        tickAmount: manyPoints ? 10 : undefined,
        labels: { ...axisLabel, rotate: 0, hideOverlappingLabels: true },
        axisBorder: { show: false },
        axisTicks: { show: false },
        tooltip: { enabled: false },
      },
      yaxis: {
        min: 0,
        forceNiceScale: true,
        labels: { ...axisLabel, formatter: fmtMoneyShort },
      },
      tooltip: { y: { formatter: (v) => fmtMoney(v) } },
    },
    series: hasData ? [{ name: "Revenue", data: revenue }] : [],
  };
}

/* ---------- Sales by menu category (cid 1 / chid 2) ---------- */
export function salesByCategory(serverOptions = {}, serverSeries = []) {
  const cats = serverOptions?.xaxis?.categories || [];
  const revenue = sumSeries(serverSeries, /revenue/i);
  const items = sumSeries(serverSeries, /orders/i);

  let rows = cats
    .map((name, i) => ({
      name: prettyName(name),
      revenue: toNum(revenue[i]),
      items: toNum(items[i]),
    }))
    .filter((r) => r.revenue > 0)
    .sort((a, b) => b.revenue - a.revenue);

  if (rows.length > MAX_CATEGORIES) {
    const rest = rows.slice(MAX_CATEGORIES - 1);
    rows = rows.slice(0, MAX_CATEGORIES - 1).concat({
      name: `Other (${rest.length})`,
      revenue: rest.reduce((a, r) => a + r.revenue, 0),
      items: rest.reduce((a, r) => a + r.items, 0),
    });
  }

  const total = rows.reduce((a, r) => a + r.revenue, 0);
  const opts = base({
    type: "bar",
    title: "Sales by category",
    subtitle: rows.length ? `${rows.length} categories · ${fmtMoney(total)}` : "",
    horizontal: true,
  });

  const hasOther = rows.length && /^Other \(/.test(rows[rows.length - 1].name);

  return {
    options: {
      ...opts,
      // "Other" is a remainder, not a category: mute it.
      colors: rows.map((r, i) => (hasOther && i === rows.length - 1 ? "#cbd5e1" : BRAND)),
      dataLabels: {
        enabled: true,
        textAnchor: "start",
        offsetX: 4,
        style: { fontSize: "11px", fontWeight: 500, colors: ["#111827"] },
        formatter: (v) => (total ? `${Math.round((v / total) * 100)}%` : ""),
      },
      plotOptions: {
        bar: { ...opts.plotOptions.bar, distributed: true, dataLabels: { position: "top" } },
      },
      // Share labels on the bars carry the scale; amounts are in the tooltip.
      grid: { ...opts.grid, xaxis: { lines: { show: false } } },
      xaxis: {
        categories: rows.map((r) => r.name),
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: { ...axisLabel, maxWidth: 140, style: { ...axisLabel.style, colors: "#374151" } },
      },
      tooltip: {
        y: {
          formatter: (v, { dataPointIndex }) => {
            const r = rows[dataPointIndex];
            return r ? `${fmtMoney(v)} · ${r.items} item${r.items === 1 ? "" : "s"}` : fmtMoney(v);
          },
        },
      },
    },
    series: rows.length ? [{ name: "Revenue", data: rows.map((r) => +r.revenue.toFixed(2)) }] : [],
    height: Math.max(220, rows.length * 36 + 90),
  };
}

/* ---------- Average party size by hour (cid 3 / chid 2) ---------- */
export function partySizeByHour(serverOptions = {}, serverSeries = []) {
  let categories = (serverOptions?.xaxis?.categories || []).map(String);
  // A party can't average zero guests; the server uses 0 for "no tables seated".
  let data = (serverSeries[0]?.data || []).map((v) => (toNum(v) > 0 ? toNum(v) : null));

  const t = trimToActive(categories, [data]);
  categories = t.categories;
  data = t.seriesData[0];

  const values = data.filter((v) => v != null);
  const hasData = values.length > 0;
  const peak = hasData ? Math.max(...values) : 0;

  const opts = base({
    type: "bar",
    title: "Average party size by hour",
    subtitle: hasData ? `Largest parties: ${peak} guests` : "",
  });

  return {
    options: {
      ...opts,
      colors: ["#475569"],
      xaxis: {
        categories,
        labels: { ...axisLabel, rotate: 0, hideOverlappingLabels: true },
        axisBorder: { show: false },
        axisTicks: { show: false },
        tooltip: { enabled: false },
      },
      yaxis: {
        min: 0,
        forceNiceScale: true,
        labels: { ...axisLabel, formatter: (v) => toNum(v).toFixed(0) },
      },
      tooltip: {
        y: { formatter: (v) => (v == null ? "No tables" : `${v} guests per table`) },
      },
    },
    series: hasData ? [{ name: "Guests per party", data }] : [],
  };
}
